import {
  PolicyList,
  PolicyPage,
  PolicyParagraph,
  PolicySection,
} from "./policy/PolicyPage";

/**
 * Terms of Service. Built from the shared `PolicyPage` kit so it matches the
 * cookie and privacy policies. Product is the Artisaire Guestbook Service;
 * operator/contact is Artisaire.
 */
export function TermsOfService() {
  return (
    <PolicyPage title="Terms of Service" lastUpdated="June 12, 2026">
      <PolicySection title="Agreement">
        <PolicyParagraph>
          These Terms of Service (&ldquo;Terms&rdquo;) govern your use of
          Artisaire Guestbook (the &ldquo;Service&rdquo;), operated by Artisaire.
        </PolicyParagraph>
        <PolicyParagraph>
          By accessing or using the Service, you agree to be bound by these
          Terms.
        </PolicyParagraph>
        <PolicyParagraph>
          If you do not agree, do not use the Service.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Eligibility">
        <PolicyParagraph>
          You must be at least the age of majority in your jurisdiction to use
          the Service.
        </PolicyParagraph>
        <PolicyParagraph>
          By using the Service, you represent that you have the authority to
          enter into these Terms.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Accounts">
        <PolicyParagraph>You may be required to create an account.</PolicyParagraph>
        <PolicyParagraph>You agree to:</PolicyParagraph>
        <PolicyList
          items={[
            "Provide accurate information",
            "Keep account credentials secure",
            "Promptly update account information",
            "Accept responsibility for activity occurring under your account",
          ]}
        />
      </PolicySection>

      <PolicySection title="Service Description">
        <PolicyParagraph>
          The Service enables users to collect, organize, manage, store, export,
          and share contact information and related event information.
        </PolicyParagraph>
        <PolicyParagraph>
          We may modify, suspend, or discontinue features at any time.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="User Content">
        <PolicyParagraph>
          &ldquo;User Content&rdquo; means any information, data, files,
          contacts, notes, or materials submitted through the Service.
        </PolicyParagraph>
        <PolicyParagraph>
          You retain ownership of your User Content.
        </PolicyParagraph>
        <PolicyParagraph>
          You grant us a limited, non-exclusive license to host, store, process,
          reproduce, and transmit User Content solely for the purpose of
          operating and improving the Service.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Responsibility for Uploaded Information">
        <PolicyParagraph>
          You are solely responsible for all User Content.
        </PolicyParagraph>
        <PolicyParagraph>You represent and warrant that:</PolicyParagraph>
        <PolicyList
          items={[
            "You have all necessary rights to upload the information.",
            "Your use of the Service complies with applicable laws.",
            "You have any required permissions or consent from individuals whose information is stored or shared through the Service.",
          ]}
        />
      </PolicySection>

      <PolicySection title="Sharing and Exports">
        <PolicyParagraph>
          The Service allows users to export or share information with third
          parties.
        </PolicyParagraph>
        <PolicyParagraph>You acknowledge that:</PolicyParagraph>
        <PolicyList
          items={[
            "Exports occur at your direction.",
            "Recipients are selected by you.",
            "You are responsible for determining whether sharing is lawful and appropriate.",
          ]}
        />
        <PolicyParagraph>
          We are not responsible for the actions of third parties who receive
          information that you choose to share.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Prohibited Uses">
        <PolicyParagraph>You may not:</PolicyParagraph>
        <PolicyList
          items={[
            "Violate laws or regulations",
            "Upload unlawful content",
            "Upload malicious code",
            "Interfere with system operations",
            "Attempt unauthorized access",
            "Use the Service to send spam",
            "Infringe intellectual property rights",
            "Harvest user information without authorization",
          ]}
        />
      </PolicySection>

      <PolicySection title="Intellectual Property">
        <PolicyParagraph>
          The Service and all associated software, designs, trademarks, and
          content are owned by Artisaire or its licensors.
        </PolicyParagraph>
        <PolicyParagraph>
          Except as expressly permitted, no rights are granted to you.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Feedback">
        <PolicyParagraph>
          Any suggestions or feedback you provide may be used by us without
          restriction or compensation.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Availability">
        <PolicyParagraph>
          We strive to provide reliable access but do not guarantee
          uninterrupted availability.
        </PolicyParagraph>
        <PolicyParagraph>
          The Service may be unavailable due to maintenance, updates, outages,
          or circumstances beyond our control.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Disclaimer of Warranties">
        <PolicyParagraph>
          THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
          AVAILABLE.&rdquo;
        </PolicyParagraph>
        <PolicyParagraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
          EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS
          FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Limitation of Liability">
        <PolicyParagraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, ARTISAIRE SHALL NOT BE LIABLE
          FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
        </PolicyParagraph>
        <PolicyParagraph>
          IF LIABILITY IS FOUND, OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER
          OF:
        </PolicyParagraph>
        <PolicyList
          items={[
            "(A) $100 CAD; OR",
            "(B) THE AMOUNT YOU PAID TO US DURING THE TWELVE MONTHS PRECEDING THE CLAIM.",
          ]}
        />
        <PolicyParagraph>
          IF THE SERVICE IS PROVIDED WITHOUT CHARGE, LIABILITY SHALL BE LIMITED
          TO THE MAXIMUM EXTENT PERMITTED BY LAW.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Indemnification">
        <PolicyParagraph>
          You agree to defend, indemnify, and hold harmless Artisaire from claims
          arising out of:
        </PolicyParagraph>
        <PolicyList
          items={[
            "Your use of the Service",
            "Your User Content",
            "Your violation of these Terms",
            "Your violation of applicable laws or third-party rights",
          ]}
        />
      </PolicySection>

      <PolicySection title="Termination">
        <PolicyParagraph>
          You may stop using the Service at any time.
        </PolicyParagraph>
        <PolicyParagraph>
          We may suspend or terminate accounts for violations of these Terms or
          other conduct that may harm the Service or users.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Governing Law">
        <PolicyParagraph>
          These Terms are governed by the laws of British Columbia and the laws
          of Canada applicable therein.
        </PolicyParagraph>
        <PolicyParagraph>
          Any disputes shall be resolved in the courts located in British
          Columbia, Canada.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Changes to Terms">
        <PolicyParagraph>
          We may update these Terms from time to time.
        </PolicyParagraph>
        <PolicyParagraph>
          Continued use of the Service after changes become effective
          constitutes acceptance of the revised Terms.
        </PolicyParagraph>
      </PolicySection>

      <PolicySection title="Contact">
        <PolicyParagraph>
          Questions about these Terms may be directed to:
        </PolicyParagraph>
        <p className="type-body-base text-text">Artisaire</p>
        <p className="type-body-base text-text-muted">
          Email:{" "}
          <a
            href="mailto:info@artisaire.com"
            className="text-brand underline-offset-4 hover:underline"
          >
            info@artisaire.com
          </a>
        </p>
      </PolicySection>
    </PolicyPage>
  );
}
